import { el } from '../dom';
import { isPhoneLayout } from '../mobile/layout';
import { swingDamage } from '../combat/damage';

/**
 * Phone geometry for EVERY overlay card, applied by one class.
 *
 * The dozen farm panels each hard-code `w-[460px] max-w-[92vw] max-h-[86vh]`.
 * `86vh` is 86% of the WHOLE viewport, but the overlay starts below the page
 * chrome — so on a 390px-tall phone a centred card ran ~60px past the bottom edge
 * and its footer buttons (including "Craft") were unreachable. Rather than edit
 * twelve cards, the wrapper stretches the card to exactly the space it owns and
 * docks it to one edge; `.tb-compact-panel` in app.css does the overriding, and
 * the card's own `overflow-y-auto` then scrolls INSIDE the screen.
 */
function applyCompact(wrap: HTMLElement, card: HTMLElement): void {
  if (!isPhoneLayout()) return;
  wrap.classList.remove('items-center', 'justify-center');
  wrap.classList.add('items-stretch', 'justify-end');
  // `sheet-card` is the OTHER phone geometry (a bottom sheet, `max-width:640px`).
  // A portrait phone matches both, and the two fight over `position`/`bottom` with
  // matching `!important`s. The drawer wins here because it is the one that also
  // works in landscape, so the losing rule is removed rather than out-specified.
  card.classList.remove('sheet-card');
  card.classList.add('tb-compact-panel');
}

/**
 * Bottom edge (in CSS px) of the lowest visible page chrome — the game header and
 * the nav bar. Both are measured rather than assumed, so the answer follows
 * immersive mode, the header toggle and any responsive height change.
 *
 * `offsetParent === null` is the "not rendered" test: a hidden header must count
 * as 0, otherwise immersive mode would still reserve a strip for it.
 */
export function chromeBottom(): number {
  return Math.max(0, ...['.tb-header', 'nav'].map((sel) => {
    const node = document.querySelector(sel) as HTMLElement | null;
    if (!node || !node.offsetParent) return 0;
    return Math.round(node.getBoundingClientRect().bottom);
  }));
}

/**
 * The standard centred-card overlay, docked BELOW the page chrome.
 *
 * Every farm panel used to mount as `fixed inset-0` with a dark backdrop, which
 * covered the header (z-30): the coin/shroom readout, the settings button and the
 * nav all disappeared behind a dimmed sheet, and clicks on them hit the backdrop
 * and merely closed the panel. Starting the overlay at the chrome's bottom edge
 * keeps the header visible AND clickable while a panel is open — the owner's
 * standing complaint.
 *
 * The backdrop is the click-to-close target, so it must be the element itself,
 * never a child: `onBackdrop` fires only when the click landed on the overlay.
 */
/**
 * Same geometry as `modalWrap` but with NO dimming.
 *
 * In-world dialogs — the ones you open by walking up to a thing and clicking it —
 * must not grey out the thing you are standing next to (the owner asked for the
 * shade gone; demolish.ts was the first to drop it). The element is still there
 * and still catches the click that closes the panel; it is simply transparent.
 */
export function clearWrap(onBackdrop: () => void, card: HTMLElement, cls = 'z-[65]'): HTMLElement {
  const top = chromeBottom();
  const wrap = el('div',
    `fixed left-0 right-0 bottom-0 ${cls} flex items-center justify-center`,
    { style: `top:${top}px`, onClick: (e: Event) => { if (e.target === wrap) onBackdrop(); } },
    card);
  applyCompact(wrap, card);
  return wrap;
}

/**
 * The pack's 44px inventory cell — the bag's own slot art.
 *
 * Lives here rather than inside the bag panel because the Forager Hut's counter
 * shows the SAME cells on both sides of the trade; two hand-rolled copies would
 * drift the moment the slot art changes. 44px is also the minimum tap target.
 */
export function slotCell(label: string, ...children: (HTMLElement | string)[]): HTMLElement {
  return el('div', 'relative w-11 h-11 flex-none flex items-center justify-center', {
    title: label,
    style: 'background:url(/sprites/ui/hotbar/slot.png) center / contain no-repeat;image-rendering:pixelated',
  }, ...children);
}

/** The count chip in a slot's bottom-right corner. */
export function qtyBadge(qty: number): HTMLElement {
  return el('span', 'absolute -bottom-0.5 -right-0.5 text-[10px] font-mono text-white leading-none px-0.5 rounded bg-black/60', {},
    qty > 9999 ? '9k+' : String(qty));
}

// ─── The premade inventory board ────────────────────────────────────────────
/**
 * The bag used to be drawn on the pack's BOOK plate: two absolutely-positioned
 * columns floating inside a 224:133 picture of an open book, with the item list
 * simply wrapping until it ran out of page. The owner asked for the pack's
 * *inventory* art instead — `UI/UI/UI_Premade.png`, which ships a ready-made
 * board: an equipment panel, a coin/attack/defence readout under it, a 5x5 bag
 * grid and a five-slot tool row along the bottom.
 *
 * Nothing here is drawn or invented. The sheet is used AS IS: one <div> shows the
 * board region as a background at an integer-ish scale, and every slot is a known
 * rectangle inside it that we position content over. That is why these numbers are
 * raw sheet pixels — they were measured off the sheet's own slot interiors (the
 * flat `#b86f50` holes), so a slot drawn here lands exactly in a painted hole.
 *
 * All of it is pure data + pure functions so the layout contract can be asserted
 * without a layout engine — jsdom would happily "pass" a board of zero-size slots.
 */
export const PREMADE_SHEET = '/pack/UI/UI/UI_Premade.png';
/** Natural size of the sheet, needed to scale the background. */
export const PREMADE_SHEET_SIZE = { w: 512, h: 256 } as const;
/** The board (equipment + stats + bag + tool row) inside the sheet. */
export const BOARD = { x: 36, y: 28, w: 194, h: 156 } as const;
/** One painted slot hole. Not square — the sheet's holes are 16x15. */
export const SLOT = { w: 16, h: 15 } as const;
/** The 5x5 bag grid: top-left hole plus the pitch between holes. */
export const BAG_GRID = { x: 114, y: 35, pitch: 23, cols: 5, rows: 5 } as const;
/** How many stacks fit on one page. Paging exists because the grid cannot grow. */
export const BAG_PAGE_SIZE = BAG_GRID.cols * BAG_GRID.rows;
/** The bottom row — five holes, one per craftable tool slot. */
export const TOOL_ROW = { x: 114, y: 161, pitch: 23, count: 5 } as const;
/** The equipment panel's middle column: helmet, chest, legs, top to bottom. */
export const ARMOUR_COL = { x: 64, ys: [35, 57, 79] } as const;
/**
 * The three recessed bars on the stats panel, in sheet pixels. The sheet already
 * paints the coin, sword and shield pictograms to their left, so these carry the
 * NUMBER only — drawing our own icons over the art would double them up.
 */
export const STAT_BARS = {
  coins: { x: 59, y: 114, w: 38, h: 10 },
  attack: { x: 56, y: 133, w: 41, h: 10 },
  defence: { x: 60, y: 152, w: 37, h: 10 },
} as const;

/** Minimum tap target (Apple HIG / Material). A slot smaller than this is a bug. */
export const MIN_TAP_PX = 44;

/**
 * How much to blow the board up by, given the space it may occupy.
 *
 * Capped at 4x (beyond that a 16px hole is a dinner plate) and floored at the
 * scale that still yields a 44px tap target — on a 320px phone the board is then
 * WIDER than the screen and its wrapper scrolls, which is the honest trade: a
 * 5-column grid of legal tap targets simply cannot fit 320px, and shrinking the
 * slots to make it fit would break the touch floor instead of admitting it.
 * Quarter steps rather than whole ones so mid-size windows are not thrown a
 * whole scale step away from the space they actually have.
 */
export function boardScale(availW: number, availH: number): number {
  const min = MIN_TAP_PX / SLOT.w;                       // 2.75
  const raw = Math.min(availW / BOARD.w, availH / BOARD.h, 4);
  return Math.max(min, Math.floor(raw * 4) / 4);
}

/** Absolute placement of a sheet-pixel rect inside the scaled board. */
export function boardRect(x: number, y: number, w: number, h: number, scale: number): string {
  return `position:absolute;left:${(x - BOARD.x) * scale}px;top:${(y - BOARD.y) * scale}px;`
    + `width:${w * scale}px;height:${h * scale}px`;
}

/** The board plate itself: the sheet, scaled, cropped to the board region. */
export function boardPlate(scale: number, ...children: HTMLElement[]): HTMLElement {
  return el('div', 'relative flex-none', {
    style: `width:${BOARD.w * scale}px;height:${BOARD.h * scale}px;`
      + `background-image:url(${PREMADE_SHEET});`
      + `background-size:${PREMADE_SHEET_SIZE.w * scale}px ${PREMADE_SHEET_SIZE.h * scale}px;`
      + `background-position:${-BOARD.x * scale}px ${-BOARD.y * scale}px;`
      + 'background-repeat:no-repeat;image-rendering:pixelated',
  }, ...children);
}

/** Sheet-pixel rect of bag cell `i` (0..24), reading left-to-right, top-to-bottom. */
export function bagCellRect(i: number): { x: number; y: number; w: number; h: number } {
  const col = i % BAG_GRID.cols;
  const row = Math.floor(i / BAG_GRID.cols);
  return { x: BAG_GRID.x + col * BAG_GRID.pitch, y: BAG_GRID.y + row * BAG_GRID.pitch, w: SLOT.w, h: SLOT.h };
}

/** Sheet-pixel rect of tool-row slot `i` (0..4). */
export function toolCellRect(i: number): { x: number; y: number; w: number; h: number } {
  return { x: TOOL_ROW.x + i * TOOL_ROW.pitch, y: TOOL_ROW.y, w: SLOT.w, h: SLOT.h };
}

/** How many pages of 25 a stack list needs. Always at least one (an empty bag). */
export function bagPageCount(total: number, per: number = BAG_PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / per));
}

/** Keep a page index inside the pages that exist — the list shrinks as you spend. */
export function clampBagPage(page: number, total: number, per: number = BAG_PAGE_SIZE): number {
  return Math.min(Math.max(0, Math.floor(page)), bagPageCount(total, per) - 1);
}

/** The stacks shown on `page`, clamped so an out-of-range page is never blank. */
export function bagPageSlice<T>(items: T[], page: number, per: number = BAG_PAGE_SIZE): T[] {
  const p = clampBagPage(page, items.length, per);
  return items.slice(p * per, p * per + per);
}

/** The tier of each equipment/tool slot, as `GET /crafting` reports it. */
export type SlotTiers = Partial<Record<string, number>>;

/**
 * ATTACK, as shown beside the sheet's sword.
 *
 * Delegates to `combat/damage.ts swingDamage`, which is itself the documented
 * mirror of the server's `crafting/gear.ts weaponDamage` — so the number in the
 * bag is the number the swing lands with, fist baseline included. Deriving it
 * here from the tier would have been a third copy of the ladder.
 */
export function bagAttack(tiers: SlotTiers): number {
  return swingDamage(tiers.sword ?? 0);
}

/**
 * DEFENCE, as shown beside the sheet's shield: the flat damage worn armour soaks.
 *
 * Mirrors `backend/src/game/crafting/gear.ts armorDefense` exactly — chest counts
 * double because it is the big piece. There is no client copy of that helper to
 * import (armour mitigation is applied server-side in FieldService.damage), so
 * this is the mirror, and it must be changed in lockstep with gear.ts.
 */
export function bagDefence(tiers: SlotTiers): number {
  return (tiers.helmet ?? 0) + (tiers.chest ?? 0) * 2 + (tiers.legs ?? 0);
}

export function modalWrap(onBackdrop: () => void, card: HTMLElement, cls = 'z-40'): HTMLElement {
  const top = chromeBottom();
  // `tb-game-modal` is a HANDLE, not a style. These are appended to document.body,
  // not to the game shell, so unmounting the game did not take them with it: sign
  // out with a panel open and it stayed on screen, floating over the marketing
  // site. bootstrap.ts's teardown sweeps this class off the body. Owner: "game is
  // game, website is website."
  const wrap = el('div',
    `tb-game-modal fixed left-0 right-0 bottom-0 ${cls} flex items-center justify-center bg-black/60`,
    { style: `top:${top}px`, onClick: (e: Event) => { if (e.target === wrap) onBackdrop(); } },
    card);
  applyCompact(wrap, card);
  return wrap;
}
